// frontend/src/pages/AdminUsuariosPage.js
import React, { useState, useEffect } from 'react';
import { usuarioService } from '../services/usuarioService';
import {
    Dialog, DialogActions, DialogContent, DialogTitle, TextField, Button,
    Snackbar, Alert, Select, MenuItem, InputLabel, FormControl, IconButton, InputAdornment
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PageHeader from '../components/ui/PageHeader';
import BadgeEstado from '../components/ui/BadgeEstado';

const textFieldSx = {
    mb: 2,
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        fontFamily: 'var(--font-family-base)',
    },
    '& .MuiInputLabel-root': {
        fontFamily: 'var(--font-family-base)',
    },
};

const AdminUsuariosPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Estado para el modal de creación/edición
    const [modalOpen, setModalOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [formData, setFormData] = useState({ nombre: '', email: '', password: '', rol: 'RECEPCION' });
    const [showPassword, setShowPassword] = useState(false);

    // Estado para el diálogo de confirmación de eliminación
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    // Estado para notificaciones (snackbar)
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const ROLES = ['ADMIN', 'ART', 'RTI', 'RECEPCION', 'SUPERVISOR', 'PROGRAMADOR']; // Roles disponibles
    const ROL_TONO = {
        ADMIN: 'admin',
        ART: 'art',
        RTI: 'rti',
        RECEPCION: 'recepcion',
        SUPERVISOR: 'supervisor',
        PROGRAMADOR: 'programador',
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await usuarioService.getAll();
            setUsers(response.data);
            setError('');
        } catch (err) {
            setError('No se pudo cargar la lista de usuarios. Es posible que no tengas los permisos necesarios.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleOpenModal = (user = null) => {
        setCurrentUser(user);
        if (user) {
            setFormData({ nombre: user.nombre, email: user.email, password: '', rol: user.rol });
        } else {
            setFormData({ nombre: '', email: '', password: '', rol: 'RECEPCION' });
        }
        setShowPassword(false);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setCurrentUser(null);
        setShowPassword(false);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveUser = async () => {
        try {
            const dataToSave = { ...formData };
            if (currentUser && !dataToSave.password) {
                delete dataToSave.password;
            }

            if (currentUser) {
                await usuarioService.update(currentUser.id, dataToSave);
                setSnackbar({ open: true, message: 'Usuario actualizado correctamente', severity: 'success' });
            } else {
                await usuarioService.create(dataToSave);
                setSnackbar({ open: true, message: 'Usuario creado correctamente', severity: 'success' });
            }
            fetchUsers();
            handleCloseModal();
        } catch (err) {
            console.error("Error al guardar usuario:", err);
            setSnackbar({ open: true, message: `Error al guardar usuario: ${err.response?.data?.message || err.message}`, severity: 'error' });
        }
    };

    const handleOpenDeleteDialog = (user) => {
        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setUserToDelete(null);
    };

    const confirmDelete = async () => {
        if (userToDelete) {
            try {
                await usuarioService.remove(userToDelete.id);
                setSnackbar({ open: true, message: 'Usuario eliminado correctamente', severity: 'success' });
                fetchUsers();
            } catch (err) {
                console.error("Error al eliminar usuario:", err);
                setSnackbar({ open: true, message: 'Error al eliminar el usuario', severity: 'error' });
            } finally {
                handleCloseDeleteDialog();
            }
        }
    };

    const handleValidarUsuario = async (userId) => {
        try {
            await usuarioService.validar(userId);
            setSnackbar({ open: true, message: 'Usuario validado correctamente. Ahora puede iniciar sesión.', severity: 'success' });
            fetchUsers();
        } catch (err) {
            console.error("Error al validar usuario:", err);
            setSnackbar({ open: true, message: 'Error al validar el usuario', severity: 'error' });
        }
    };

    const handleInvalidarUsuario = async (userId) => {
        try {
            await usuarioService.invalidar(userId);
            setSnackbar({ open: true, message: 'Validación revocada. El usuario ya no puede iniciar sesión.', severity: 'success' });
            fetchUsers();
        } catch (err) {
            console.error("Error al invalidar usuario:", err);
            setSnackbar({ open: true, message: 'Error al invalidar el usuario', severity: 'error' });
        }
    };

    if (loading) {
        return (
            <div className="text-center my-5">
                <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }}></div>
                <p className="mt-3 fw-semibold">Cargando usuarios...</p>
            </div>
        );
    }

    if (error) {
        return <div className="container mt-4 alert alert-danger">{error}</div>;
    }

    return (
        <div className="area-admin">
            <div className="card border-0" style={{ borderRadius: '12px', boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
                <div className="card-body p-0">
                    <PageHeader
                        variante="banner"
                        icono="people-fill"
                        titulo="Administración de Usuarios"
                        descripcion="Gestión de accesos, roles y cuentas del personal"
                        accion={
                            <button
                                className="btn btn-light d-flex align-items-center gap-2 px-4 py-2"
                                onClick={() => handleOpenModal()}
                                style={{
                                    borderRadius: '8px',
                                    fontWeight: '600',
                                    fontFamily: 'var(--font-family-base)',
                                    boxShadow: 'var(--shadow-sm)'
                                }}
                            >
                                <i className="bi bi-plus-circle-fill"></i>
                                Crear Usuario
                            </button>
                        }
                    />

                    {/* Tabla moderna */}
                    <div className="px-4 pb-4">
                        <div className="table-responsive">
                            <table className="table align-middle" style={{ marginBottom: 0 }}>
                                <thead>
                                <tr style={{
                                    background: 'var(--gradient-primary-light)',
                                    borderBottom: '2px solid #3B82F6'
                                }}>
                                    <th style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', fontFamily: 'var(--font-family-base)' }}>ID</th>
                                    <th style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', fontFamily: 'var(--font-family-base)' }}>Nombre</th>
                                    <th style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', fontFamily: 'var(--font-family-base)' }}>Email</th>
                                    <th style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', fontFamily: 'var(--font-family-base)' }}>Rol</th>
                                    <th style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', fontFamily: 'var(--font-family-base)' }}>Estado</th>
                                    <th style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', fontFamily: 'var(--font-family-base)', textAlign: 'center' }}>Acciones</th>
                                </tr>
                                </thead>
                                <tbody>
                                {users.map(user => (
                                    <tr key={user.id} style={{
                                        borderBottom: '1px solid #e5e7eb',
                                        transition: 'background-color 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                        <td style={{ padding: '1rem', fontFamily: 'var(--font-family-base)' }}>{user.id}</td>
                                        <td style={{ padding: '1rem', fontWeight: '500', fontFamily: 'var(--font-family-base)' }}>{user.nombre}</td>
                                        <td style={{ padding: '1rem', color: '#64748b', fontFamily: 'var(--font-family-base)' }}>{user.email}</td>
                                        <td style={{ padding: '1rem' }}>
                                            {user.rol ? (
                                                <BadgeEstado tono={ROL_TONO[user.rol] || 'neutro'}>
                                                    {user.rol}
                                                </BadgeEstado>
                                            ) : (
                                                <BadgeEstado tono="advertencia">
                                                    Sin Rol
                                                </BadgeEstado>
                                            )}
                                        </td>
                                        <td style={{ padding: '1rem' }}>
                                            {user.validado ? (
                                                <BadgeEstado tono="exito" icono="check-circle-fill">
                                                    Validado
                                                </BadgeEstado>
                                            ) : (
                                                <BadgeEstado tono="advertencia" icono="clock-fill">
                                                    Pendiente
                                                </BadgeEstado>
                                            )}
                                        </td>
                                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                                            <div className="d-flex justify-content-center gap-2">
                                                {!user.validado && (
                                                    <button 
                                                        className="btn btn-sm"
                                                        onClick={() => handleValidarUsuario(user.id)}
                                                        title="Validar usuario"
                                                        style={{
                                                            background: '#10B981',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            padding: '0.5rem 1rem',
                                                            fontWeight: '600',
                                                            fontFamily: 'var(--font-family-base)',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                        }}
                                                    >
                                                        <i className="bi bi-check-lg"></i>
                                                        Validar
                                                    </button>
                                                )}
                                                {user.validado && (
                                                    <button 
                                                        className="btn btn-sm"
                                                        onClick={() => handleInvalidarUsuario(user.id)}
                                                        title="Revocar validación"
                                                        style={{
                                                            background: '#F59E0B',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            padding: '0.5rem 1rem',
                                                            fontWeight: '600',
                                                            fontFamily: 'var(--font-family-base)',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                        }}
                                                    >
                                                        <i className="bi bi-x-lg"></i>
                                                        Invalidar
                                                    </button>
                                                )}
                                                <button 
                                                    className="btn btn-sm"
                                                    onClick={() => handleOpenModal(user)}
                                                    style={{
                                                        background: '#3B82F6',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        padding: '0.5rem 0.75rem',
                                                        display: 'inline-flex',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <i className="bi bi-pencil-fill"></i>
                                                </button>
                                                <button 
                                                    className="btn btn-sm"
                                                    onClick={() => handleOpenDeleteDialog(user)}
                                                    style={{
                                                        background: '#EF4444',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        padding: '0.5rem 0.75rem',
                                                        display: 'inline-flex',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <i className="bi bi-trash-fill"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de Creación/Edición */}
            <Dialog
                open={modalOpen}
                onClose={handleCloseModal}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: '16px', overflowY: 'auto' } }}
            >
                <DialogTitle
                    sx={{
                        background: 'var(--primary)',
                        color: '#fff',
                        fontFamily: 'var(--font-family-base)',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        py: 2.25,
                    }}
                >
                    {currentUser ? <EditIcon /> : <PersonAddAlt1Icon />}
                    {currentUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
                </DialogTitle>
                <DialogContent sx={{ pt: 3, mt: 1 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        name="nombre"
                        label="Nombre Completo"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formData.nombre}
                        onChange={handleFormChange}
                        sx={textFieldSx}
                    />
                    <TextField
                        margin="dense"
                        name="email"
                        label="Email"
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={formData.email}
                        onChange={handleFormChange}
                        sx={textFieldSx}
                    />
                    <TextField
                        margin="dense"
                        name="password"
                        label={currentUser ? "Nueva Contraseña (opcional)" : "Contraseña"}
                        type={showPassword ? 'text' : 'password'}
                        fullWidth
                        variant="outlined"
                        value={formData.password}
                        onChange={handleFormChange}
                        sx={textFieldSx}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <FormControl fullWidth margin="dense" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                        <InputLabel sx={{ fontFamily: 'var(--font-family-base)' }}>Rol</InputLabel>
                        <Select name="rol" label="Rol" value={formData.rol} onChange={handleFormChange} sx={{ fontFamily: 'var(--font-family-base)' }}>
                            {ROLES.map(rol => <MenuItem key={rol} value={rol}>{rol}</MenuItem>)}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        onClick={handleCloseModal}
                        sx={{ fontFamily: 'var(--font-family-base)', fontWeight: 600, borderRadius: '8px', color: '#64748b' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSaveUser}
                        variant="contained"
                        sx={{
                            fontFamily: 'var(--font-family-base)',
                            fontWeight: 700,
                            borderRadius: '8px',
                            px: 3,
                            background: 'var(--primary)',
                            boxShadow: 'var(--shadow-md)',
                        }}
                    >
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo de Confirmación de Eliminación */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                PaperProps={{ sx: { borderRadius: '16px', overflowY: 'auto' } }}
            >
                <DialogTitle
                    sx={{
                        background: 'var(--gradient-danger)',
                        color: '#fff',
                        fontFamily: 'var(--font-family-base)',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        py: 2.25,
                    }}
                >
                    <WarningAmberIcon />
                    Confirmar Eliminación
                </DialogTitle>
                <DialogContent sx={{ pt: 3, mt: 1, fontFamily: 'var(--font-family-base)', color: '#334155' }}>
                    ¿Estás seguro de que deseas eliminar al usuario "{userToDelete?.nombre}"? Esta acción no se puede deshacer.
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        onClick={handleCloseDeleteDialog}
                        sx={{ fontFamily: 'var(--font-family-base)', fontWeight: 600, borderRadius: '8px', color: '#64748b' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        variant="contained"
                        color="error"
                        sx={{ fontFamily: 'var(--font-family-base)', fontWeight: 700, borderRadius: '8px', px: 3 }}
                    >
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar para notificaciones */}
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default AdminUsuariosPage;
